/**
 * Helpers for the role-aware permission list used in user create / edit.
 *
 * Each permission row carries two comma-separated role lists (from
 * permissions_master):
 *   - permission_applicable_roles : role codes that SEE the checkbox
 *                                   (empty / null => visible to every role)
 *   - permission_default_roles    : role codes for which the checkbox starts checked
 */

export interface PermissionRow {
  permission_code: string;
  permission_label: string;
  permission_applicable_roles?: string | null;
  permission_default_roles?: string | null;
  [key: string]: any;
}

const splitRoles = (csv?: string | null): string[] =>
  (csv || '')
    .split(',')
    .map(r => r.trim().toLowerCase())
    .filter(Boolean);

/**
 * Permissions a given role should see, unioned with any already-granted codes.
 *
 * The union with `grantedCodes` matters for the edit-user flow: a user may hold
 * a permission that falls outside their role's normal set (e.g. granted manually
 * or before the role changed). Keeping it visible (and checked) means an
 * unrelated save never silently revokes it.
 *
 * A row with no applicable roles is treated as visible to everyone.
 */
export const getVisiblePermissions = (
  permissionsList: PermissionRow[] = [],
  role?: string | null,
  grantedCodes: string[] = []
): PermissionRow[] => {
  const target = (role || '').trim().toLowerCase();
  const granted = new Set(grantedCodes);
  return permissionsList.filter(p => {
    if (granted.has(p.permission_code)) return true;
    const roles = splitRoles(p.permission_applicable_roles);
    if (roles.length === 0) return true; // applies to all roles
    return target ? roles.includes(target) : false;
  });
};

/**
 * Permission codes that should be checked by default for a given role.
 */
export const getDefaultPermissionCodes = (
  permissionsList: PermissionRow[] = [],
  role?: string | null
): string[] => {
  const target = (role || '').trim().toLowerCase();
  if (!target) return [];
  return permissionsList
    .filter(p => splitRoles(p.permission_default_roles).includes(target))
    .map(p => p.permission_code);
};
